import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const AppContext = createContext();

export function AppProvider({ children }) {
  const [theme, setTheme] = useState('light');
  const [toast, setToast] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const logoutTimerRef = useRef(null);
  const TOKEN_STORAGE_KEY = 'admin_token_v1';

  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const scheduleAutoLogout = useCallback((token) => {
    clearLogoutTimer();
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (!payload.exp) return; // pas d'exp => pas de timer
      const ms = payload.exp * 1000 - Date.now();
      if (ms <= 0) {
        setAdminToken(null);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        return;
      }
      logoutTimerRef.current = setTimeout(() => {
        setAdminToken(null);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }, ms);
    } catch (_) {
      // token invalide => purge
      setAdminToken(null);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    // Restaure le token au chargement (si encore valide)
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (stored) {
        const payload = JSON.parse(atob(stored.split('.')[1] || ''));
        if (payload.exp && payload.exp * 1000 > Date.now()) {
          setAdminToken(stored);
          scheduleAutoLogout(stored);
        } else {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
      }
    } catch (_) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
    // Sync multi‑onglets
    const onStorage = (e) => {
      if (e.key === TOKEN_STORAGE_KEY) {
        if (!e.newValue) {
          setAdminToken(null);
          clearLogoutTimer();
        } else {
          const val = e.newValue;
            try {
              const p = JSON.parse(atob(val.split('.')[1] || ''));
              if (p.exp && p.exp * 1000 > Date.now()) {
                setAdminToken(val);
                scheduleAutoLogout(val);
              } else {
                localStorage.removeItem(TOKEN_STORAGE_KEY);
                setAdminToken(null);
              }
            } catch { /* ignore */ }
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearLogoutTimer();
    };
  }, [scheduleAutoLogout]);

  const showToast = useCallback((message, type = 'success', duration = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), duration);
  }, []);

  const loginAdmin = useCallback((token) => {
    setAdminToken(token);
    try { localStorage.setItem(TOKEN_STORAGE_KEY, token); } catch (_) { /* ignore */ }
    scheduleAutoLogout(token);
  }, [scheduleAutoLogout]);

  const logoutAdmin = useCallback(() => {
    setAdminToken(null);
    try { localStorage.removeItem(TOKEN_STORAGE_KEY); } catch (_) { /* ignore */ }
    clearLogoutTimer();
  }, []);

  // Déconnexion automatique si un service API reçoit un 401 (token expiré/révoqué)
  useEffect(() => {
    const handle = () => {
      logoutAdmin();
      showToast('Session expirée, veuillez vous reconnecter', 'warning');
    };
    window.addEventListener('auth:unauthorized', handle);
    return () => window.removeEventListener('auth:unauthorized', handle);
  }, [logoutAdmin, showToast]);

  const value = {
    theme,
    setTheme,
    toast,
    showToast,
    adminToken,
    loginAdmin,
    logoutAdmin,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  return useContext(AppContext);
}
